/* Sign-up and onboarding adapter: core auth routes (api/README.md: signup, login, me, Google start,
   password forgot/reset) and the billing module's onboarding state machine (GET/POST /api/v1/onboarding,
   /onboarding/estimate), plus public plans and the connectors used by the "Connect apps" step. */
import { ApiError, api } from '../../lib/api';
import type {
  AuthResponse, Frequency, OnboardingEstimate, OnboardingResponse, OnboardingStepBody, OnboardingStepResult, PlansResponse,
} from '../billing/types';

export interface SignupBody { email: string; password: string; dob: string; name?: string }
export interface EstimateBody { frequency: Frequency; kinds: string[]; ownKeys: boolean }

export interface ConnectorChoice { id: string; name: string; available: boolean }

export interface WelcomeBackend {
  me(): Promise<AuthResponse | null>;
  signup(body: SignupBody): Promise<AuthResponse>;
  login(email: string, password: string): Promise<AuthResponse>;
  logout(): Promise<void>;
  /** Google OAuth start URL (state + PKCE kept by the API); throws 503 google_not_configured. */
  googleStart(redirect: string): Promise<string>;
  forgot(email: string): Promise<{ ok: boolean; token?: string }>;
  reset(token: string, password: string): Promise<void>;
  onboarding(): Promise<OnboardingResponse>;
  step(body: OnboardingStepBody): Promise<OnboardingStepResult>;
  estimate(body: EstimateBody): Promise<OnboardingEstimate>;
  plans(): Promise<PlansResponse | null>;
  connections(): Promise<string[]>;
  /** Connectors from GET /api/v1/connectors/catalog; available only when its OAuth client is set up in the admin portal. */
  catalog(): Promise<ConnectorChoice[]>;
  /** OAuth start URL for a connector, or null when it was connected at once (API-key connectors). */
  connect(id: string, redirect: string): Promise<string | null>;
  /** Legal document from the safety module (terms, privacy, ai-transparency), or null when unavailable. */
  legal(slug: string): Promise<{ title: string; body: string } | null>;
}

const enc = encodeURIComponent;

export const httpWelcome: WelcomeBackend = {
  me: async () => {
    try { return await api.get<AuthResponse>('/api/v1/auth/me'); } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null;
      throw e;
    }
  },
  signup: (body) => api.post<AuthResponse>('/api/v1/auth/signup', body),
  login: (email, password) => api.post<AuthResponse>('/api/v1/auth/login', { email, password }),
  logout: async () => { await api.post('/api/v1/auth/logout'); },
  googleStart: async (redirect) => (await api.get<{ url: string }>('/api/v1/auth/google/start', { query: { redirect, format: 'json' } })).url,
  forgot: (email) => api.post('/api/v1/auth/password/forgot', { email }),
  reset: async (token, password) => { await api.post('/api/v1/auth/password/reset', { token, password }); },
  onboarding: () => api.get<OnboardingResponse>('/api/v1/onboarding'),
  step: (body) => api.post<OnboardingStepResult>('/api/v1/onboarding', body),
  estimate: async (body) => (await api.post<{ estimate: OnboardingEstimate }>('/api/v1/onboarding/estimate', body)).estimate,
  plans: async () => { try { return await api.get<PlansResponse>('/api/v1/billing/plans'); } catch { return null; } },
  connections: async () => {
    try {
      const r = await api.get<{ connections: Array<{ connectorId?: string; connector?: string; status?: string }> }>('/api/v1/connectors/connections');
      return (r.connections ?? []).filter((c) => c.status !== 'revoked').map((c) => c.connectorId ?? c.connector ?? '').filter(Boolean);
    } catch { return []; }
  },
  catalog: async () => {
    const r = await api.get<{ connectors: Array<{ id: string; name?: string; oauthConfigured?: boolean; disabledByOrg?: boolean; auth?: { type?: string } }> }>('/api/v1/connectors/catalog');
    return (r.connectors ?? []).map((c) => ({ id: c.id, name: c.name ?? c.id, available: !c.disabledByOrg && (c.auth?.type !== 'oauth2' || !!c.oauthConfigured) }));
  },
  connect: async (id, redirect) => {
    try {
      const r = await api.get<{ authUrl: string }>(`/api/v1/connectors/${enc(id)}/oauth/start`, { query: { redirect, format: 'json' } });
      return r.authUrl;
    } catch (e) {
      // No OAuth client in the admin portal: say so; never make a stand-in connection.
      if (e instanceof ApiError && (e.code === 'oauth_not_configured' || e.status === 409)) {
        throw new ApiError(503, 'not_configured', 'This app cannot be connected yet: its sign-in is not set up in the admin portal.', { setting: 'Connector sign-in', adminPath: '/omni/connectors' });
      }
      throw e;
    }
  },
  legal: async (slug) => {
    try {
      const r = await api.get<{ document: { title: string; body: string } }>(`/api/v1/legal/documents/${enc(slug)}`);
      return r.document ? { title: r.document.title, body: r.document.body } : null;
    } catch { return null; }
  },
};

export function welcomeError(e: unknown): string {
  if (e instanceof ApiError) {
    // A 503 with the API's own code (google_not_configured) carries a useful message; only a missing service is "network".
    if (e.isNetwork && (e.code === 'network' || e.code.startsWith('http_'))) return "Can't reach Crosspoint AI +. Nothing was lost; check your connection and try again.";
    return e.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong.';
}
